import { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';

interface NavigationItem {
  id: string;
  label: string;
  href: string;
}

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);

  const navigationItems: NavigationItem[] = [
    { id: 'home', label: 'Home', href: '#home' },
    { id: 'about', label: 'About', href: '#about' },
    { id: 'capabilities', label: 'Capabilities', href: '#capabilities' },
    { id: 'industries', label: 'Industries', href: '#industries' },
    { id: 'process', label: 'Process', href: '#process' },
    { id: 'contact', label: 'Contact', href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = navigationItems.map(item => item.id);
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }

    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled ? 'glass-effect shadow-glass' : 'bg-transparent'
        } ${className}`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <a
              href="https://dhavaja.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 transition-opacity duration-250 hover:opacity-80"
            >
              <Image 
                src="/assets/images/Dhavaja_Logo_Text_White-1765121120872.png"
                alt="Dhavaja Logo"
                className="h-10 w-auto object-contain"
              />
            </a>

            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-250 ${
                    activeSection === item.id
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center space-x-4">
              <a
                href="https://www.linkedin.com/company/dhavaja"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-all duration-250"
                aria-label="Visit Dhavaja LinkedIn"
              >
                <Icon name="Linkedin" size={20} />
              </a>
            </div>

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md text-foreground hover:bg-muted/50 transition-all duration-250"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[90] bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        className={`fixed top-0 right-0 bottom-0 z-[150] w-80 max-w-[85vw] glass-effect shadow-glass-lg transform transition-transform duration-250 lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <span className="text-foreground font-semibold text-lg">Menu</span>
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md text-foreground hover:bg-muted/50 transition-all duration-250"
              aria-label="Close mobile menu"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto p-6">
            <div className="space-y-2">
              {navigationItems.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`block px-4 py-3 rounded-md text-base font-medium transition-all duration-250 ${
                    activeSection === item.id
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </nav>

          <div className="p-6 border-t border-border">
            <a
              href="https://www.linkedin.com/company/dhavaja"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 px-4 py-3 rounded-md text-accent bg-accent/10 hover:bg-accent/20 transition-all duration-250"
            >
              <Icon name="Linkedin" size={20} />
              <span className="font-medium">Connect on LinkedIn</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;